import { usePermission, useSupabase } from "@how/app-kit";
import { Card, Stack, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { ErrorState, LoadingState, TimeZoneField, friendlyZoneName } from "@how/ui";
import { useTenantRecord } from "../tenant/queries";

/** Words for a refusal from set_tenant_time_zone: the database's own sentence for a broken rule, ours otherwise. */
export function timeZoneErrorMessage(error: { code?: string; message: string }): string {
  if (error.code === "23514") return error.message;
  if (error.code === "42501") return "Only the church owner can change the time zone.";
  if (error.code === "P0002") return "This church was not found.";
  return "The time zone was not changed. Try again.";
}

/**
 * The church's time zone (owner decision of 2026-10-03). Only the owner changes it (settings.time_zone, owner-only);
 * everyone else who reaches Settings sees it with the reason. Stored times do not move; what is read in the church's
 * zone follows it.
 */
export function TimeZoneCard() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();
  const record = useTenantRecord();
  const canChange = usePermission("settings.time_zone", { write: true });

  const save = async (zone: string): Promise<void> => {
    const tenantId = record.data?.id ?? "";
    const { error } = await supabase.rpc("set_tenant_time_zone", {
      p_tenant_id: tenantId,
      p_time_zone: zone,
    });
    if (error) throw new Error(timeZoneErrorMessage(error));
    notifications.show({
      color: "teal",
      title: "Time zone changed",
      message: `Your church now runs on ${friendlyZoneName(zone)} time.`,
    });
    // Every screen that shows a time reads the church's zone; let them all read it again.
    void queryClient.invalidateQueries();
  };

  return (
    <Card>
      <Stack gap="sm">
        <Title order={2} size="h4">
          Time zone
        </Title>
        {record.isPending || canChange.isPending ? (
          <LoadingState rows={2} />
        ) : record.isError ? (
          <ErrorState
            message="Could not load the church's time zone."
            onRetry={() => {
              void record.refetch();
            }}
          />
        ) : (
          <TimeZoneField
            value={record.data.time_zone}
            canChange={canChange.data === true}
            lockedMessage="Only the church owner can change this."
            churchName={record.data.name}
            onSave={save}
          />
        )}
      </Stack>
    </Card>
  );
}
